export const package_options = [
  {
    badge: "Most Requested",
    description:
      "Signature HD Bridal Makeup, Editorial Hair Design & Bespoke Sari / Dupatta Draping",
    label: "All-Inclusive Full Bridal Package",
    value: "all-inclusive",
  },
  {
    badge: "",
    description:
      "Luminous complexion styling, lashes, contouring & tailored textured bridal updo",
    label: "Bridal Makeup + Hair Styling",
    value: "makeup-hair",
  },
  {
    badge: "",
    description:
      "Full luxury face styling paired with precision pleated pin-work and veil security",
    label: "Bridal Makeup + Sari / Dupatta Draping",
    value: "makeup-draping",
  },
  {
    badge: "",
    description: "Customized long-wear editorial makeup for bride only",
    label: "Bridal Makeup Artistry (Stand-Alone)",
    value: "makeup-primary",
  },
  {
    badge: "",
    description:
      "Special event glam for mothers, sisters, bridal parties, or gala attendance",
    label: "Party / Bridesmaid / Family Only",
    value: "party-guest",
  },
] as const;

export const party_size_options = [
  { label: "Bride Only (1 person)", value: "1" },
  { label: "Bride + 1 to 2 family members (2-3 total)", value: "2-3" },
  { label: "Bride + Bridal Party (4-6 total)", value: "4-6" },
  { label: "Large Bridal Party (7-10 total — assistant needed)", value: "7-10" },
  { label: "Extended Wedding Group (10+ people)", value: "10+" },
] as const;

export const event_options = [
  { label: "Mehndi / Haldi", value: "mehndi_haldi" },
  { label: "Sangeet Night", value: "sangeet" },
  { label: "Morning Ceremony", value: "morning_ceremony" },
  { label: "Evening Reception", value: "evening_reception" },
] as const;

export type inquiry_search_params = {
  [key: string]: string | string[] | undefined;
};

export type inquiry_prefill = {
  event_date: string;
  first_name: string;
  last_name: string;
  notes: string;
  package_value: string;
  venue: string;
};

export type inquiry_payload = {
  email: string;
  event_date: string;
  events: string[];
  first_name: string;
  last_name: string;
  multi_day: boolean;
  notes: string;
  package_value: string;
  party_size: string;
  phone: string;
  ready_by_time: string;
  venue: string;
  venue_tbd: boolean;
};

export const empty_prefill: inquiry_prefill = {
  event_date: "",
  first_name: "",
  last_name: "",
  notes: "",
  package_value: "all-inclusive",
  venue: "",
};

const package_values = new Set<string>(package_options.map((option) => option.value));
const party_size_values = new Set<string>(party_size_options.map((option) => option.value));
const event_values = new Set<string>(event_options.map((option) => option.value));

export function package_label(value: string) {
  return package_options.find((option) => option.value === value)?.label ?? value;
}

export function party_size_label(value: string) {
  return party_size_options.find((option) => option.value === value)?.label ?? value;
}

export function event_label(value: string) {
  return event_options.find((option) => option.value === value)?.label ?? value;
}

export function package_from_services(services: string) {
  const text = services.toLowerCase();
  const draping = text.includes("drap") || text.includes("sari");
  const hair = text.includes("hair");
  const makeup = text.includes("makeup");
  const party = text.includes("bridesmaid") || text.includes("party");

  if (makeup && hair && draping) return "all-inclusive";
  if (makeup && hair) return "makeup-hair";
  if (makeup && draping) return "makeup-draping";
  if (makeup) return "makeup-primary";
  if (hair) return "makeup-hair";
  if (draping) return "makeup-draping";
  if (party) return "party-guest";
  return "all-inclusive";
}

function read_query(query: inquiry_search_params, key: string) {
  const value = query[key];
  if (Array.isArray(value)) return value[0] ?? "";
  return value ?? "";
}

export function prefill_from_query(query: inquiry_search_params): inquiry_prefill {
  const name = read_query(query, "name").trim();
  const space_index = name.indexOf(" ");
  const services = read_query(query, "services").trim();

  return {
    event_date: read_query(query, "date"),
    first_name: space_index === -1 ? name : name.slice(0, space_index),
    last_name: space_index === -1 ? "" : name.slice(space_index + 1).trim(),
    notes: services ? `Services requested from the homepage: ${services}` : "",
    package_value: services ? package_from_services(services) : "all-inclusive",
    venue: read_query(query, "venue"),
  };
}

function read_string(source: Record<string, unknown>, key: string) {
  const value = source[key];
  return typeof value === "string" ? value.trim() : "";
}

function read_boolean(source: Record<string, unknown>, key: string) {
  return source[key] === true;
}

function read_string_list(source: Record<string, unknown>, key: string) {
  const value = source[key];
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string");
}

export function validate_inquiry(
  input: unknown,
): { errors: Record<string, string>; ok: false } | { ok: true; value: inquiry_payload } {
  if (!input || typeof input !== "object") {
    return { errors: { form: "Enter your inquiry details." }, ok: false };
  }

  const source = input as Record<string, unknown>;
  const errors: Record<string, string> = {};
  const email = read_string(source, "email");
  const event_date = read_string(source, "event_date");
  const events = read_string_list(source, "events");
  const first_name = read_string(source, "first_name");
  const last_name = read_string(source, "last_name");
  const multi_day = read_boolean(source, "multi_day");
  const notes = read_string(source, "notes");
  const package_value = read_string(source, "package_value");
  const party_size = read_string(source, "party_size") || "1";
  const phone = read_string(source, "phone");
  const ready_by_time = read_string(source, "ready_by_time");
  const venue_tbd = read_boolean(source, "venue_tbd");
  let venue = read_string(source, "venue");

  if (!first_name) errors.first_name = "Enter your first name.";
  if (!last_name) errors.last_name = "Enter your last name.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = "Enter a valid email address.";
  if (phone.replace(/\D/g, "").length < 10) errors.phone = "Enter a phone number with area code.";
  if (!package_values.has(package_value)) errors.package_value = "Choose a service.";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(event_date)) errors.event_date = "Choose your event date.";
  if (ready_by_time && !/^\d{2}:\d{2}$/.test(ready_by_time)) {
    errors.ready_by_time = "Enter a ready-by time.";
  }
  if (venue_tbd) {
    venue = venue || "Flexible / Venue TBD";
  } else if (!venue) {
    errors.venue = "Enter a venue or city.";
  }
  if (!party_size_values.has(party_size)) errors.party_size = "Choose a bridal party size.";
  if (events.some((event_name) => !event_values.has(event_name))) {
    errors.events = "Choose events from the list.";
  }
  if (notes.length > 5000) errors.notes = "Shorten your notes to 5000 characters.";

  if (Object.keys(errors).length > 0) return { errors, ok: false };

  return {
    ok: true,
    value: {
      email,
      event_date,
      events: multi_day ? events : [],
      first_name,
      last_name,
      multi_day,
      notes,
      package_value,
      party_size,
      phone,
      ready_by_time,
      venue,
      venue_tbd,
    },
  };
}
